// src/pages/reports/ProductSummaryReport.jsx
import { useState, useEffect, useMemo } from 'react'
import { ChevronDown, X } from 'lucide-react'
import BaseReportPage from '../../components/reports/BaseReportPage'
import CategoryService from '../../services/categoryService'

const ProductSummaryFilters = ({ filters, onFiltersChange, onApply, onClear }) => {
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(true)
  
  // Load categories directly from API
  useEffect(() => {
    const loadCategories = async () => {
      try {
        setLoading(true)
        console.log('🔍 Product Summary: Directly loading categories')
        const response = await CategoryService.getAllCategories()
        console.log('✅ Product Summary: Categories response:', response)
        
        // Extract categories correctly based on response structure
        let categoriesData = []
        if (response && response.success) {
          if (response.data && response.data.categories && Array.isArray(response.data.categories)) {
            categoriesData = response.data.categories
            console.log('✅ Product Summary: Found nested categories:', categoriesData.length)
          } else if (Array.isArray(response.data)) {
            categoriesData = response.data
            console.log('✅ Product Summary: Found direct categories array:', categoriesData.length)
          }
        }
        
        setCategories(categoriesData)
      } catch (error) {
        console.error('❌ Product Summary: Error loading categories:', error)
      } finally {
        setLoading(false)
      }
    }
    
    loadCategories()
  }, [])
  
  // Mock categories as fallback
  const mockCategories = useMemo(() => [
    { _id: '1', name: 'Coffee', isActive: true },
    { _id: '2', name: 'Tea', isActive: true },
    { _id: '3', name: 'Pastries', isActive: true },
    { _id: '4', name: 'Sandwiches', isActive: true }
  ], [])
  
  // Display categories (real or mock)
  const displayCategories = categories.length > 0 ? categories : mockCategories
  
  const handleFilterChange = (key, value) => {
    onFiltersChange({ ...filters, [key]: value })
  }

  // Clear filters function
  const handleClearFilters = () => {
    onFiltersChange({
      branch: '',
      category: '',
      search: '',
      dateRange: 'today'
    })
    onClear()
  }

  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return filters.branch || filters.category || filters.search || (filters.dateRange && filters.dateRange !== 'today')
  }, [filters])
  
  return (
    <div className="bg-gray-50 p-4 rounded-lg mb-4">
      <div className="flex justify-between items-center mb-3">
        <h3 className="text-lg font-medium text-gray-900">Filters</h3>
        {hasActiveFilters && (
          <button
            onClick={handleClearFilters}
            className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-orange-600 bg-white border border-orange-300 rounded-lg hover:bg-orange-50 hover:text-orange-700 hover:border-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-opacity-50 transition-colors"
          >

            <span>Clear Filters</span>
          </button>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Branch</label>
          <div className="relative">
            <select
              value={filters.branch || ''}
              onChange={(e) => handleFilterChange('branch', e.target.value)}
              className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md text-sm appearance-none"
            >
              <option value="">All Branches</option>
              <option value="colombo">Tezlaa Colombo</option>
              <option value="negombo">Tezlaa Negombo</option>
              <option value="warehouse">Tezlaa Warehouse</option>
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
              <ChevronDown className="h-4 w-4" />
            </div>
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
          <div className="relative">
            <select
              value={filters.category || ''}
              onChange={(e) => handleFilterChange('category', e.target.value)}
              className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md text-sm appearance-none"
              disabled={loading}
            >
              <option value="">All Categories</option>
              {displayCategories.map((category) => (
                <option key={category._id} value={category._id}>
                  {category.name}
                </option>
              ))}
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
              <ChevronDown className="h-4 w-4" />
            </div>
          </div>
          {loading && <p className="text-xs text-gray-500 mt-1">Loading categories...</p>}
          {!loading && categories.length === 0 && <p className="text-xs text-amber-500 mt-1">Using default categories</p>}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Product Search</label>
          <div className="relative">
            <input
              type="text"
              placeholder="Product name..."
              value={filters.search || ''}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            />
          </div>
        </div>
      </div>
      {/* <div className="mt-4 flex justify-end space-x-2">
        <button 
          onClick={() => onApply(filters)}
          className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 transition-colors"
        >
          Apply Filters
        </button>
        <button 
          onClick={onClear}
          className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md text-sm hover:bg-gray-300 transition-colors"
        >
          Clear
        </button>
      </div> */}
    </div>
  )
}

const ProductSummaryReport = () => {
  const columns = [
    { key: 'code', label: 'Code', type: 'text' },
    { key: 'productName', label: 'Product name', type: 'text' },
    { key: 'category', label: 'Category', type: 'text' },
    { key: 'currentStock', label: 'Current Stock', type: 'number' },
    { key: 'purchasePrice', label: 'Purchase price', type: 'currency' },
    { key: 'salesPrice', label: 'Sales price', type: 'currency' },
    { key: 'branchName', label: 'Branch name', type: 'text' },
  ]

  return (
    <BaseReportPage
      reportType="product-summary"
      title="Product Summary Report"
      columns={columns}
      dataKey="data"
      enableFilters={true}
      customFilterComponent={ProductSummaryFilters}
    />
  )
}

export default ProductSummaryReport